export default function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, '');
    str = str.replace(/é|è|ê|ë/g, 'e');
    str = str.replace(/à/g, 'a');
    str = str.replace(/ç/g, 'c');
    str = str.replace(/î/g, 'i');
    str = str.replace(/ô/g, 'o');
    str = str.replace(/û/g, 'u');
    str = str.replace(/ /g, '-');
    str = str.replace(/-+/g, '-');
    str = str.toLowerCase();

    return str;
};

export function capitalize(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Nom de famille / nom de naissance : toujours affichés en capitales dans toute l'application.
export function upperCaseName(str) {
    if (!str) return str;
    return str.toUpperCase();
}

/**
 * Remplace les jetons d'équivalence (prenom-maj, il-elle-maj, ...) d'une liste de textes de
 * faire-part par les valeurs correspondant au genre du défunt.
 */
export function processFprTextEquivalences(list, deceasedInfos) {
    if (!list || !deceasedInfos) return list;

    // Tolère "MME" et "Mme" : la civilité n'est pas toujours stockée avec la même casse, et un
    // test strict sur "MME" ferait silencieusement retomber tous les jetons de genre (dont
    // "e-or-not-e") sur la version masculine dès que la casse diffère.
    const isFeminine = deceasedInfos?.civility?.toUpperCase() === 'MME';
    const hidden = deceasedInfos?.hiddenCivility;

    const equivalences = {
        'il-elle-maj': hidden ? 'Il/Elle' : (isFeminine ? 'Elle' : 'Il'),
        'il-elle-min': hidden ? 'il/elle' : (isFeminine ? 'elle' : 'il'),
        'prenom-maj': capitalize(deceasedInfos?.firstName) || '',
        'civility-maj': hidden ? '' : (isFeminine ? 'MME' : 'M'),
        'civ-maj': hidden ? '' : (isFeminine ? 'MME' : 'M'),
        'lhomme-lafemme': hidden ? 'la personne' : (isFeminine ? 'la femme' : 'l\'homme'),
        'lemari-lepouse': hidden ? 'le/la conjoint(e)' : (isFeminine ? 'l\'épouse' : 'le mari'),
        'lepere-lamere': hidden ? 'le parent' : (isFeminine ? 'la mère' : 'le père'),
        'epousemere-maripere': hidden ? 'conjoint(e), parent' : (isFeminine ?  'épouse, mère' : 'mari, père'),
        'le-la': hidden ? 'le/la' : (isFeminine ? 'la' : 'le'),
        'eor-not-e': hidden ? '(e)' : (isFeminine ? 'e' : ''),
        'e-or-not-e': hidden ? '(e)' : (isFeminine ? 'e' : ''),
        'lui-elle-min': hidden ? 'lui/elle' : (isFeminine ? 'elle' : 'lui'),
        'prenom_du_défunt': capitalize(deceasedInfos?.firstName) || '',
        'prénom_du_défunt': capitalize(deceasedInfos?.firstName) || '',
        '[date]': deceasedInfos?.ceremonyInfos?.date || '',
        '[heure]': deceasedInfos?.ceremonyInfos?.time || '',
        '[lieu de la cérémonie]': deceasedInfos?.ceremonyInfos?.placeLabel + deceasedInfos?.ceremonyInfos?.placeName || '',
        'tonserviteur-taservante': hidden ? 'ton serviteur/ta servante' : (isFeminine ? 'ta servante' : 'ton serviteur'),
        'liste-role-maj': hidden ? 'Le/la proche, le/la conjoint(e), le/la frère/sœur, l\'ami(e)' : (isFeminine ? 'Le papa, le mari, le frère, l\'ami' : 'La maman, la femme, la sœur, l\'amie'),
        'lundi-01-Janvier-decease': deceasedInfos?.deceasedDate ? new Date(deceasedInfos.deceasedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '',
    };

    const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    return list.map(item => {
        let processedText = item.text || '';

        Object.keys(equivalences).forEach(key => {
            const replacement = equivalences[key];
            processedText = processedText.replace(new RegExp(escapeRegExp(key), 'gi'), replacement);
        });

        return {
            ...item,
            text: processedText
        };
    });
}

/**
 * Accole la bonne préposition (au / à la / à l') selon le genre du lieu, l' l'emportant
 * dès que le lieu commence par une voyelle, qu'il soit masculin ou féminin.
 */
export function getPlaceWithPreposition(place) {
    if (!place) return place;

    if (/^[aeiouàâäéèêëîïôöùûüœ]/i.test(place)) {
        return `à l'${place}`;
    }
    if (/^(Cimeti[èe]re|Cr[ée]matorium|Fun[ée]rarium|Colombarium|Temple|Site|Jardin)/i.test(place)) {
        return `au ${place}`;
    }
    if (/^(Chapelle|Mairie|Salle|Maison|Propriété)/i.test(place)) {
        return `à la ${place}`;
    }
    return `à ${place}`;
}

export function replaceHomeAddress(place, deceasedInfos) {
    if (!place) return place;
    let result = place.replace(/Domicile du défunt/gi, `Chez ${deceasedInfos?.firstName || 'le défunt'}`);

    if (/^(à|au|à la|à l'|chez|dans)/i.test(result)) {
        return result;
    }
    else if (result && !result.startsWith('Chez')) {
        result = getPlaceWithPreposition(result);
    }

    return result;
}

export function replaceHomeRecAddress(place) {
    if (!place) return place;
    if (/Domicile du défunt/i.test(place)) {
        return `à son domicile`;
    }
    return getPlaceWithPreposition(place);
}

export function formatFprAddress(address) {
    if (!address) return address;
    return address.replace(/\bMR\b/g, 'M').replace(/\bMr\b/g, 'M').replace(/\bmr\b/g, 'M');
}

/**
 * Calcule le contenu par dépublicationfaut d'une rubrique de faire-part dérivée des événements du défunt
 * (cérémonie, recueillement, verre du souvenir, adresse de correspondance).
 */
export function getFprEventDefaultContent(contentType, { eventList, deceasedInfos, deceasedEventInfos }) {
    // Adresse de correspondance ne dépend que de deceasedInfos (pas des événements) : ce garde ne
    // doit donc pas exiger deceasedEventInfos, sous peine de bloquer ce cas quand il vaut null
    // (ex: appelé sans passer par la récupération des événements, comme pour le remerciement).
    // Les autres cas gèrent déjà une valeur manquante via le chaînage optionnel ci-dessous.
    if (!deceasedInfos) return '';

    switch (contentType) {
        case 'Information cérémonie': {
            if (!eventList || eventList.length === 0) {
                return '<p>Pour la cérémonie funéraire, la famille souhaite rester dans l\'intimité familiale.</p>';
            }

            const hasCeremonyReligieuse = eventList?.some(event =>
                event?.typeKey === "Ceremonie" && [1, 2, 3, 4, 5].includes(event?.placeTypeKey)
            );
            const hasCeremonyNonReligieuse = eventList?.some(event =>
                event?.typeKey === "Ceremonie" && ![1, 2, 3, 4, 5].includes(event?.placeTypeKey)
            );
            const hasCremation = eventList?.some(event => event?.typeKey === "Cremation");
            const hasInhumation = eventList?.some(event => event?.typeKey === "Obseques");

            let dateCeremony = '';
            let placeCeremony = '';
            let hourCremation = '';
            let placeCremation = '';
            let hourInhumation = '';
            let placeInhumation = '';

            if (hasCeremonyReligieuse || hasCeremonyNonReligieuse) {
                dateCeremony = `${deceasedEventInfos?.ceremonyDateNotFormatted}, à ${deceasedEventInfos?.ceremonyTime}`;
                placeCeremony = replaceHomeAddress(deceasedEventInfos?.ceremonyPlace, deceasedInfos);
            }

            if (hasCremation) {
                hourCremation = deceasedEventInfos?.cremationTime;
                placeCremation = replaceHomeAddress(deceasedEventInfos?.cremationPlace, deceasedInfos);
            }

            if (hasInhumation) {
                hourInhumation = deceasedEventInfos?.obsequesTime;
                placeInhumation = replaceHomeAddress(deceasedEventInfos?.obsequesPlace, deceasedInfos);
            }

            let result = '';

            if (hasCeremonyReligieuse && hasInhumation && hasCremation) {
                result = `<p>Ses funérailles auront lieu le ${dateCeremony} ${placeCeremony}, suivies de la crémation à ${hourCremation} à ${placeCremation}, suivies de l'inhumation à ${hourInhumation} à ${placeInhumation}.</p>`;

            } else if (hasCeremonyReligieuse && hasCremation) {
                result = `<p>Ses funérailles auront lieu le ${dateCeremony} ${placeCeremony}, suivies de la crémation à ${hourCremation} à ${placeCremation}.</p>`;

            } else if (hasCeremonyReligieuse && hasInhumation) {
                result = `<p>Ses funérailles auront lieu le ${dateCeremony} ${placeCeremony}, suivies de l'inhumation à ${hourInhumation} à ${placeInhumation}.</p>`;

            } else if (hasCeremonyNonReligieuse && hasCremation) {
                result = `<p>Un hommage civil lui sera rendu le ${dateCeremony}, ${placeCeremony}, suivi de la crémation à ${placeCremation} à ${hourCremation}.</p>`;

            } else if (hasCeremonyNonReligieuse && hasInhumation) {
                result = `<p>Un hommage civil lui sera rendu le ${dateCeremony}, ${placeCeremony}, suivi de l'inhumation à ${hourInhumation} à ${placeInhumation}.</p>`;

            } else if (hasCeremonyReligieuse) {
                result = `<p>Ses obsèques religieuses seront célébrées le ${dateCeremony} ${placeCeremony}.</p>`;

            } else if (hasCeremonyNonReligieuse) {
                result = `<p>Un hommage civil lui sera rendu le ${dateCeremony}, ${placeCeremony}.</p>`;

            } else if (hasCremation) {
                result = `<p>Ses funérailles auront lieu à ${hourCremation} ${placeCremation}.</p>`;

            } else if (hasInhumation) {
                result = `<p>Ses funérailles auront lieu à ${hourInhumation} ${placeInhumation}.</p>`;
            }

            return result || '<p>Pour la cérémonie funéraire, la famille souhaite rester dans l\'intimité familiale.</p>';
        }

        case 'Information recueillement':
            if (eventList?.some(event => event?.typeKey === "Recueillement")) {
                if (deceasedInfos?.firstName) {
                    return `<p>${deceasedInfos.firstName} repose ${replaceHomeRecAddress(deceasedEventInfos?.recueillementPlace)}.</p>`;
                }
            }
            return '';

        case 'Information verre du souvenir':
            if (eventList?.some(event => event?.typeKey === "VinHonneur")) {
                const drinkType = deceasedEventInfos?.vinHonneurType ? "repas funéraire" : "verre du Souvenir";
                return `<p>Au domicile du défunt : un ${drinkType} est organisé à ${deceasedEventInfos?.vinHonneurTime} .</p>`;
            }
            return '';

        case 'Adresse de correspondance':
            if (deceasedInfos?.conjointInfos) {
                return `<p>${formatFprAddress(deceasedInfos.conjointInfos)}</p>`;
            } else if (deceasedInfos?.gestionnaireInfos) {
                return `<p>${formatFprAddress(deceasedInfos.gestionnaireInfos)}</p>`;
            } else if (deceasedInfos?.agencyInfos) {
                return `<p>${formatFprAddress(deceasedInfos.agencyInfos)}</p>`;
            }
            return '';

        default:
            return '';
    }
}

/**
 * Accorde le participe "Décédé" au genre du défunt dans un texte de faire-part.
 */
export function adaptFprGenderText(text, deceasedInfos) {
    if (!text || !deceasedInfos?.civility) return text;

    if (deceasedInfos?.hiddenCivility) {
        return text.replace(/Décédé(?!e|\(e\))/g, 'Décédé(e)');
    }

    if (deceasedInfos.civility.toUpperCase() === 'MME' || deceasedInfos.civility === 'Mme') {
        return text.replace(/Décédé(?!e)/g, 'Décédée');
    }

    return text;
}

export function ucfirst(str) {
    if (!str) return str;
    return str
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('-');
}

export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            resolve(event.target.result);
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsDataURL(file);
    });
}


export function filterCountryList(countryList, countryData) {
    if (!countryList || !Array.isArray(countryList)) {
        return "";
    }
    const country = countryList.find((country) => country.code === countryData);
    return country ? country.name : "";
}

export const fullMaleTitles = {
    'me': 'Maître',
    'dr': 'Docteur',
    'pr': 'Professeur',
    'aud': 'Auditeur',
    'ir': 'Ingénieur civil',
    'ing': 'Ingénieur industriel',
    'arch': 'Architecte',
    'duc': 'Duc',
    'mis': 'Marquis',
    'cte': 'Comte',
    'vte': 'Vicomte',
    'bon': 'Baron',
    'sgr': 'Seigneur',
    'cher': 'Chevalier',
    'ec': 'Ecuyer',
    'sa': 'Son Altesse',
    'sai': 'Son Altesse Impériale',
    'sar': 'Son Altesse Royale',
    'sas': 'Son Altesse Sérénissime',
    'se': 'Son Excellence',
    'sm': 'Sa Majesté',
    'smi': 'Sa Majesté Impériale',
    'dom': 'Dom',
    'don': 'Don',
    'p': 'Père',
    'ab': 'Monsieur l\'Abbé',
    'cur': 'Monsieur le Curé',
    'rp': 'Révérend Père',
    'fr': 'Frère',
    'past': 'Pasteur',
    'rvd': 'Révérend',
    'mgr': 'Monseigneur',
    'imam': 'Imam',
    'rab': 'Rabbin',
    'rav': 'Rav',
    'sem': 'Son Eminence',
    'ss': 'Sa Sainteté',
};

export const fullFemaleTitles = {
    'me': 'Maître',
    'dr': 'Docteure',
    'pr': 'Professeure',
    'aud': 'Auditrice',
    'ir': 'Ingénieure civile',
    'ing': 'Ingénieure industrielle',
    'arch': 'Architecte',
    'duc': 'Duchesse',
    'mis': 'Marquise',
    'cte': 'Comtesse',
    'vte': 'Vicomtesse',
    'bon': 'Baronne',
    'cher': 'Chevalier',
    'ec': 'Ecuyère',
    'sa': 'Son Altesse',
    'sai': 'Son Altesse Impériale',
    'sar': 'Son Altesse Royale',
    'sas': 'Son Altesse Sérénissime',
    'se': 'Son Excellence',
    'sm': 'Sa Majesté',
    'smi': 'Sa Majesté Impériale',
    'dom': 'Dame',
    'p': 'Mère',
    'ab': 'Madame l\'Abbé',
    'rp': 'Révérende Mère',
    'fr': 'Soeur',
    'past': 'Pasteure',
    'rvd': 'Révérende',
    'imam': 'Imame',
    'rab': 'Rabbin',
    'rav': 'Rav',
    'sem': 'Son Eminence',
    'ss': 'Sa Sainteté',
};

export function validateFormInput(e) {
    if (!e.target || !e.target.validity) {
        return;
    }

    const { value, validity, required } = e.target;
    if (required === true && value.trim() === "") {
        e.target.parentElement.classList.add("invalid");
        e.target.parentElement.classList.remove("valid");
    } else if (!required === true && value.trim() === "") {
        e.target.parentElement.classList.remove("invalid");
        e.target.parentElement.classList.add("valid");
    } else if (!validity.valid) {
        e.target.parentElement.classList.add("invalid");
        e.target.parentElement.classList.remove("valid");
    } else {
        e.target.parentElement.classList.remove("invalid");
        e.target.parentElement.classList.add("valid");
    }
}

export function checkPasswordMatch(password, passwordConfirm) {
    if (password?.length < 8 || passwordConfirm?.length < 8) {
        return false;
    }
    return password === passwordConfirm;
}

export const getDeep = (values, path) => {
    if (!values) {
        return null;
    }

    if (path.includes('.')) {
        const [first, ...rest] = path.split('.');
        return getDeep(values[first], rest.join('.'));
    }

    return values[path];
}

export const getErrorListFromValidation = (validation) => {
    if (!validation) {
        return;
    }
    let fieldset = document.querySelector('fieldset div.errors') || document.querySelector('div.errors');
    if (!fieldset) {
        return;
    }
    for (let singleValidation of validation) {
        // La plupart des endpoints renvoient des messages en texte brut, mais certains (ex:
        // DocumentController) renvoient des objets {field, message, value} : sans ça, le message
        // affiché était littéralement "[object Object]".
        let messageText = (singleValidation && typeof singleValidation === 'object')
            ? (singleValidation.message || JSON.stringify(singleValidation))
            : singleValidation;
        let spanContent = `<p class="validation-message">${messageText}</p>`;
        fieldset.insertAdjacentHTML('beforeend', spanContent);
    }
}

export const displayMessage = (message) => {
    removeMessageList();
    if (!message) {
        return;
    }
    let type = message?.type === "error" ? "error" : "success";
    let fieldset = document.querySelector('fieldset div.message');
    let spanContent = `<p class="${type}">${message.text}</p>`;
    if (fieldset) {
        fieldset.insertAdjacentHTML('beforeend', spanContent);
    }
}

export const removeErrorList = () => {
    let spanElement = document.querySelectorAll('.validation-message');
    for (let span of spanElement) {
        span.remove();
    }
}

export const removeMessageList = () => {
    let spanElement = document.querySelectorAll('.message p');
    for (let span of spanElement) {
        span.remove();
    }
}

export const checkRequiredFields = (formElement = null) => {
    removeErrorList();
    const context = formElement || document;
    const requiredFields = context.querySelectorAll('[required]');
    let hasError = false;

    for (let field of requiredFields) {
        // Ignore les champs masqués (ex: une étape de wizard non active, display:none sur un
        // ancêtre) : un scope de validation trop large (formElement absent ou incorrect) ne doit
        // jamais marquer en invalide des champs qu'on n'a pas encore atteints.
        if (field.offsetParent === null) {
            continue;
        }
        // Un champ disabled (ex: civilité désactivée quand "Masquer la civilité" est coché) est
        // exempté de la validation de contraintes native (field.validity.valid le reflète déjà),
        // mais value.trim() === "" ci-dessous l'ignore : sans ce garde, un select disabled et vide
        // bloque quand même la soumission.
        if (field.disabled) {
            continue;
        }
        if (field.value.trim() === "" || !field.validity.valid) {
            field.parentElement.classList.add("invalid");
            field.parentElement.classList.remove("valid");
            hasError = true;
        } else {
            field.parentElement.classList.remove("invalid");
            field.parentElement.classList.add("valid");
        }
    }

    if (hasError) {
        const errorDiv = (formElement || document).querySelector('fieldset div.errors');
        if (errorDiv) {
            errorDiv.insertAdjacentHTML('beforeend', '<p class="validation-message">Veuillez remplir les champs obligatoires selon le format demandé.</p>');
        }
        return false;
    } else {
        return true;
    }
}

export const patternListChoices = {
    address: "^[A-Za-zÀ-ÖØ-öø-ÿ0-9'., \\-]{2,100}$",
    alphaNum: "^[A-Za-z0-9 .\\-]{1,}$",
    city: "^[A-Za-z0-9À-ÖØ-öø-ÿ' \\-]{2,100}$",
    email: "^[a-zA-Z0-9._\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,4}$",
    fileName: "^[A-Za-z0-9\\-_]{1,100}$",
    firstname: "^[A-Za-zÀ-ÖØ-öø-ÿ \\-]{1,}$",
    hhMM: "^([01]?[0-9]|2[0-3]):[0-5][0-9]{5}$",
    lastname: "^[A-Za-zÀ-ÖØ-öø-ÿ' \\-]{1,}$",
    latitude: "^[\\-+]?([1-8]?\\d(\\.\\d+)?|90(\\.0+)?){1,}$",
    longitude: "^[\\-+]?(180(\\.0+)?|((1[0-7]\\d)|(\\d{1,2}))(\\.\\d+)?){1,}$",
    name: "^[A-Za-z0-9À-ÖØ-öø-ÿ ,\\-:.'\\(\\)']{1,}$",
    num: "^[0-9]+{1,}$",
    orias: "^[0-9]{14}$",
    password: "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$",
    phone: "^(?:\\+\\d{1,3}\\s?)?(?:\\d\\s?){8,9}\\d$",
    price: "^[0-9]+(,[0-9]{1,2})?$",
    section: "^[0-9]+[A-Z]?$",
    siret: "^\\d{3} ?\\d{3} ?\\d{3} ?\\d{5}$",
    tvaIntra: "^[A-Z]{2}( ?[A-Z0-9]){8,12}$",
    url: "^(https?:\\/\\/)?(www\\.)?[a-z0-9\\-]+(\\.[a-z0-9\\-]+)*(\\.[a-z]{2,}){1,3}(\\/[^\\s]*)?\\/?$",
    zip: "^[A-Z0-9][A-Z0-9\\- ]{2,9}[A-Z0-9]{1,}$",
};

/**
 * Tri naturel pour les sections alphanumériques (ex: 1, 2, 7, 7A, 7B, 8, 10, 10A, 10B)
 * Cette fonction compare deux sections et retourne -1, 0, ou 1 pour le tri
 *
 * @param {string|number} a - Première section à comparer
 * @param {string|number} b - Deuxième section à comparer
 * @returns {number} -1 si a < b, 0 si a === b, 1 si a > b
 */
export function naturalSectionSort(a, b) {
    const strA = String(a).toUpperCase();
    const strB = String(b).toUpperCase();

    const matchA = strA.match(/^(\d+)([A-Z]?)$/);
    const matchB = strB.match(/^(\d+)([A-Z]?)$/);

    if (!matchA || !matchB) {
        return strA.localeCompare(strB);
    }

    const numA = parseInt(matchA[1], 10);
    const numB = parseInt(matchB[1], 10);
    const letterA = matchA[2] || '';
    const letterB = matchB[2] || '';

    // D'abord comparer les nombres
    if (numA !== numB) {
        return numA - numB;
    }

    // Si les nombres sont identiques, comparer les lettres
    if (letterA === '' && letterB !== '') return -1;
    if (letterA !== '' && letterB === '') return 1;

    return letterA.localeCompare(letterB);
}

/**
 * Trier un tableau de sections avec tri naturel
 *
 * @param {Array} sections - Tableau d'objets de sections contenant une propriété 'section'
 * @returns {Array} Tableau trié
 */
export function sortSections(sections) {
    if (!sections || !Array.isArray(sections)) {
        return [];
    }
    return [...sections].sort((a, b) => naturalSectionSort(a.section, b.section));
}

/**
 * Normalise un code postal français au format 5 chiffres
 * Nettoie les espaces et complète avec des zéros à gauche si nécessaire
 *
 * @param {string} zipcode - Code postal à normaliser
 * @returns {string} Code postal normalisé (5 chiffres) ou la valeur d'origine si invalide
 */
export function normalizeZipCode(zipcode) {
    if (!zipcode || typeof zipcode !== 'string') {
        return zipcode;
    }

    // Nettoyer le code postal (supprimer espaces, tirets, etc.)
    const cleaned = zipcode.trim().replace(/[\s\-]/g, '');

    // Vérifier si c'est un code postal français (uniquement chiffres)
    if (/^\d+$/.test(cleaned)) {
        // Si c'est inférieur à 5 chiffres, compléter avec des zéros à gauche
        if (cleaned.length <= 5) {
            return cleaned.padStart(5, '0');
        }
        // Si c'est plus de 5 chiffres, tronquer à 5 (cas improbable)
        if (cleaned.length > 5) {
            return cleaned.substring(0, 5);
        }
    }

    // Retourner la valeur nettoyée si elle ne correspond pas au format français
    return cleaned || zipcode;
}

/**
 * Assemble deux libellés en évitant de répéter le premier mot du second
 * s'il est identique (insensible à la casse) au premier libellé.
 * Ex: joinWithoutRepeatingWord("Cimetière", "Cimetière de St Hilaire") => "Cimetière de St Hilaire"
 *
 * @param {string} firstWord - Premier libellé (ex: un type de lieu)
 * @param {string} rest - Second libellé, potentiellement redondant avec le premier
 * @returns {string} Libellé combiné sans répétition
 */
export function joinWithoutRepeatingWord(firstWord, rest) {
    if (!firstWord) return rest || "";
    if (!rest) return firstWord;

    const restFirstWord = rest.trim().split(" ")[0];
    if (restFirstWord?.toLowerCase() === firstWord.trim().toLowerCase()) {
        return rest;
    }

    return `${firstWord} ${rest}`;
}

/**
 * Formate les horaires d'une visite à partir de visitInfosData, en ne mentionnant
 * que les créneaux (matin / après-midi) réellement renseignés : afficher les deux
 * clauses sans condition produit un texte vide ou tronqué dès qu'un seul créneau
 * a été choisi (select "matin" / "après-midi" / "les deux").
 *
 * @param {Object} visitInfosData - { time (= début matin), morningEnd, afternoonStart, afternoonEnd }
 * @returns {string} Libellé des horaires, ou chaîne vide si aucun créneau n'est renseigné
 */
export function formatVisitSchedule(visitInfosData) {
    const morningStart = visitInfosData?.time;
    const morningEnd = visitInfosData?.morningEnd;
    const afternoonStart = visitInfosData?.afternoonStart;
    const afternoonEnd = visitInfosData?.afternoonEnd;

    const hasMorning = !!(morningStart && morningEnd);
    const hasAfternoon = !!(afternoonStart && afternoonEnd);

    if (hasMorning && hasAfternoon) {
        return `le matin de ${morningStart} à ${morningEnd} et l'après-midi de ${afternoonStart} à ${afternoonEnd}`;
    }
    if (hasMorning) {
        return `de ${morningStart} à ${morningEnd}`;
    }
    if (hasAfternoon) {
        return `de ${afternoonStart} à ${afternoonEnd}`;
    }
    return "";
}

/**
 * Normalise les codes postaux dans un objet de données de formulaire
 * Parcourt récursivement l'objet et normalise tous les champs "zipcode" trouvés
 *
 * @param {Object} data - Objet de données à normaliser
 * @returns {Object} Objet avec les codes postaux normalisés
 */
export function normalizeFormZipCodes(data) {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const normalized = Array.isArray(data) ? [...data] : { ...data };

    for (const key in normalized) {
        if (key === 'zipcode' && typeof normalized[key] === 'string') {
            normalized[key] = normalizeZipCode(normalized[key]);
        } else if (typeof normalized[key] === 'object' && normalized[key] !== null) {
            normalized[key] = normalizeFormZipCodes(normalized[key]);
        }
    }

    return normalized;
}

/**
 * Retire les balises HTML d'une chaîne pour obtenir du texte brut,
 * utile pour comparer/rechercher un contenu Tiptap dans le texte extrait d'un PDF.
 *
 * @param {string} html - Contenu HTML (ex: sortie Tiptap "<p>...</p>")
 * @returns {string} Texte brut, espaces normalisés
 */
export function stripHtml(html) {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return (tempDiv.textContent || tempDiv.innerText || '').replace(/\s+/g, ' ').trim();
}

